/**
 * Dominios, la pantalla de la cuenta.
 *
 * ── Por qué no vive en Facturación ──────────────────────────────────────────
 *
 * Porque son dos propósitos distintos y estaban en la misma caja. Facturación
 * responde «qué se me cobra y cuándo»: importes, fechas, tarjetas, cancelar la
 * anualidad. Esta pantalla responde «qué dominios tengo y dónde los uso», que no
 * es una pregunta de dinero — se entra a conectar uno a otro sitio, a mirar su
 * DNS o a llevárselo, y ninguna de las tres tiene que ver con un cobro.
 *
 * Mezcladas, la lista de dominios de Facturación tenía que ser las dos cosas a
 * la vez y no era ninguna: enseñaba la renovación pero no el estado, y su única
 * acción era cancelar.
 *
 * ── Por qué se parece tanto a Facturación ───────────────────────────────────
 *
 * Comparte su lenguaje —el mismo widget, las mismas filas, las mismas
 * pastillas— porque son pantallas hermanas de la cuenta y se llega a las dos
 * desde el mismo menú. Distinguirlas por el aspecto habría sido inventar una
 * diferencia que no existe; lo que las distingue es lo que hay dentro.
 */
import { useCallback, useEffect, useState } from 'react'
import { longDate } from '../../lib/dates'
import { EmbeddedCheckoutPanel, type CheckoutSummary } from '../ProjectWorkspace/sections/EmbeddedCheckout'
import { useI18n, translateServerError, translate } from '../../lib/i18n'
import {
  IconArrowRight,
  IconCheck,
  IconClockCheck,
  IconKey,
  IconLink,
  IconLoader2,
  IconSettings,
  IconWorld,
} from '@tabler/icons-react'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { DomainSettingsModal } from './DomainSettingsModal'
import { listAccountDomains, listProjects, reconnectProjectDomain, setDomainAutoRenew, startDomainRenewal, deleteProjectDomain, type AccountDomain, type BillingDomainCharge, type Project } from '../../lib/api'

/**
 * La misma pastilla que Facturación, con sus mismos tonos.
 *
 * Llevaba `dash-domain-badge`, que es la de las tarjetas de Configuración: allí
 * `is-primary` significa «dirección principal» y va en ámbar, así que un dominio
 * conectado se pintaba de color de aviso. Son dos vocabularios distintos y el de
 * esta pantalla es el de la cuenta.
 */
function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'muted'; children: React.ReactNode }) {
  return <em className={`dash-billing-pill is-${tone}`}>{children}</em>
}

/* Claves, no texto: constante de módulo. El estado aquí habla de la CONEXIÓN
   con un sitio; el de la ficha del dominio habla del dominio en sí, así que son
   dos juegos de palabras para el mismo campo y cada uno tiene el suyo. */
const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  active: { label: 'accountDomains.status.active', tone: 'ok' },
  verifying: { label: 'accountDomains.status.verifying', tone: 'muted' },
  pending_dns: { label: 'accountDomains.status.pending_dns', tone: 'muted' },
  purchasing: { label: 'accountDomains.status.purchasing', tone: 'muted' },
  disconnected: { label: 'accountDomains.status.disconnected', tone: 'muted' },
  error: { label: 'accountDomains.status.error', tone: 'warn' },
}

/**
 * El dominio de la cuenta, con la forma que espera el panel de ajustes.
 *
 * La ventana nació en Facturación y lee un `BillingDomainCharge`. Los campos son
 * los mismos con otro nombre, así que se adapta aquí en cuatro líneas en vez de
 * duplicar el componente o de inventar un tercer tipo que las dos pantallas
 * tengan que mantener.
 */
function asCharge(domain: AccountDomain): BillingDomainCharge {
  return {
    id: domain.id,
    hostname: domain.hostname,
    projectId: domain.projectId ?? '',
    projectName: domain.projectName ?? translate('accountDomains.notConnected'),
    status: domain.status,
    pricePaidCents: domain.priceCents,
    renewalPriceCents: domain.renewalPriceCents,
    currency: domain.currency,
    purchasedAt: domain.purchasedAt,
    renewsAt: domain.expiresAt,
    sslStatus: domain.sslStatus,
  }
}

export function AccountDomains({ token, onOpenProject }: {
  token: string
  /** Para ir al sitio donde está conectado un dominio. */
  onOpenProject?: ((projectId: string) => void) | undefined
}) {
  const { t } = useI18n()
  const [domains, setDomains] = useState<AccountDomain[] | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [failed, setFailed] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState<AccountDomain | null>(null)
  /** El proyecto elegido en cada fila, mientras no se confirma. */
  const [pick, setPick] = useState<Record<string, string>>({})
  /**
   * El pago de una renovación, cuando lo abre el cliente.
   *
   * Vive aquí y no en la fila porque el checkout embebido tapa la pantalla: es
   * un estado de la sección, no de una tarjeta. `null` mientras no hay ninguno
   * abierto, que es casi siempre.
   */
  const [renewal, setRenewal] = useState<{ clientSecret: string; summary: CheckoutSummary } | null>(null)

  const load = useCallback(async () => {
    setFailed('')
    try {
      const [list, mine] = await Promise.all([listAccountDomains(token), listProjects(token)])
      setDomains(list)
      /* Sólo donde se puede conectar: en un proyecto compartido no se tienen
         los permisos de dominio, así que ofrecerlo sería ofrecer un 403. */
      setProjects(mine.filter((project) => !project.shared))
    } catch (err) {
      setFailed(translateServerError(err, t('err.loadDomains')))
      setDomains([])
    }
  }, [token, t])

  useEffect(() => { void load() }, [load])

  /**
   * Conecta un dominio al proyecto elegido.
   *
   * Si ya estaba en otro, primero se suelta. Son dos llamadas y no una porque
   * no existe un «mover»: reconectar exige que el dominio esté suelto, y esa
   * regla es la que impide que un dominio acabe en dos sitios a la vez. Mover
   * es una acción rara —se hace una vez y no se repite— así que no compensa una
   * ruta nueva para ahorrar un viaje.
   */
  const connect = async (domain: AccountDomain, projectId: string) => {
    if (busy) return
    setBusy(domain.id)
    setError('')
    try {
      if (domain.status !== 'disconnected' && domain.projectId) {
        await deleteProjectDomain(token, domain.projectId, domain.id)
      }
      await reconnectProjectDomain(token, projectId, domain.id)
      await load()
      setPick((current) => {
        const next = { ...current }
        delete next[domain.id]
        return next
      })
    } catch (err) {
      setError(translateServerError(err, t('err.connectDomain')))
    } finally {
      setBusy(null)
    }
  }

  /**
   * Enciende o apaga el cobro automático.
   *
   * Se pinta el valor nuevo antes de que conteste el servidor y se vuelve atrás
   * si falla: es un interruptor, y un interruptor que tarda medio segundo en
   * moverse se pulsa dos veces.
   */
  const toggleAutoRenew = async (domain: AccountDomain) => {
    const next = !domain.autoRenew
    setError('')
    setDomains((current) => current?.map((row) => (
      row.id === domain.id ? { ...row, autoRenew: next } : row
    )) ?? current)
    try {
      await setDomainAutoRenew(token, domain.id, next)
    } catch (err) {
      setDomains((current) => current?.map((row) => (
        row.id === domain.id ? { ...row, autoRenew: domain.autoRenew } : row
      )) ?? current)
      setError(translateServerError(err, t('accountDomains.autoRenewFailed')))
    }
  }

  /** Abre el pago de la renovación. El mismo checkout embebido que la compra. */
  const renew = async (domain: AccountDomain) => {
    if (busy) return
    setBusy(domain.id)
    setError('')
    try {
      const { clientSecret, priceCents } = await startDomainRenewal(token, domain.id)
      setRenewal({
        clientSecret,
        summary: {
          title: domain.hostname,
          description: t('accountDomains.renewSummary'),
          features: [],
          amount: priceCents / 100,
          unit: t('accountDomains.renewUnit'),
          billed: t('accountDomains.renewBilled'),
          backLabel: t('accountDomains.title'),
          legal: t('accountDomains.renewLegal'),
        },
      })
    } catch (err) {
      setError(translateServerError(err, t('accountDomains.renewFailed')))
    } finally {
      setBusy(null)
    }
  }

  if (renewal) {
    return (
      <EmbeddedCheckoutPanel
        clientSecret={renewal.clientSecret}
        summary={renewal.summary}
        onClose={() => { setRenewal(null) }}
        onComplete={() => {
          /* El webhook renueva; aquí sólo se cierra y se recarga. La fecha nueva
             la manda el registrador, así que se lee, no se calcula. */
          setRenewal(null)
          void load()
        }}
      />
    )
  }

  return (
    <div className="dash-dashboard-view dash-standard-page">
      <DashboardPageHeader
        title={t('accountDomains.title')}
        subtitle={t('accountDomains.subtitle')}
      />

      {error ? <p className="dash-domain-error" role="alert">{error}</p> : null}

      {domains !== null && !failed && domains.length === 0 ? (
        /* El vacío es una pantalla en sí, no una lista con cabecera pero sin
           filas. Usa el mismo componente visual que los demás vacíos del
           dashboard para no presentar una tarjeta enorme y casi desocupada. */
        <section className="dash-placeholder dash-account-domains-empty">
          <IconWorld size={34} aria-hidden="true" />
          <strong>{t('accountDomains.emptyTitle')}</strong>
          <span>{t('accountDomains.emptyBody')}</span>
        </section>
      ) : (
      <section className="dash-settings-card dash-billing-widget is-domains">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconWorld size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('accountDomains.cardTitle')}</h3>
            <p>{t('accountDomains.cardBody')}</p>
          </div>
        </div>

        <div className="dash-billing-panel">
          {domains === null ? (
            <p className="dash-card-description">{t('accountDomains.loading')}</p>
          ) : failed ? (
            <div className="dash-inline-msg">
              <span>{failed}</span>
              <div className="dash-inline-msg-actions">
                <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('domain.retry')}</button>
              </div>
            </div>
          ) : domains.map((domain) => {
            /* Sin fila, el estado crudo del servidor: dice qué buscar en el
               registro, y es mejor que un hueco. No es una clave, así que no
               pasa por `t`. */
            const known = STATUS[domain.status]
            const status = known
              ? { label: t(known.label), tone: known.tone }
              : { label: domain.status, tone: 'muted' as const }
            const chosen = pick[domain.id] ?? domain.projectId ?? projects[0]?.id ?? ''
            /* Sólo hay algo que hacer si el destino cambia, o si está suelto y
               vuelve al mismo sitio. Sin esto el botón se ofrecía siempre y la
               mitad de las veces no hacía nada. */
            const canApply = domain.status === 'disconnected' || chosen !== domain.projectId
            const working = busy === domain.id

            return (
              /*
               * Dos pisos, no una fila.
               *
               * Arriba QUÉ es —icono, nombre, estado y su fecha—; abajo QUÉ se
               * hace con ello, tras un filo. Todo en un renglón eran cuatro
               * controles y dos líneas de texto peleando por el mismo ancho, y
               * lo que cedía era siempre el nombre: se partía en sílabas y la
               * fecha bajaba a una palabra por línea.
               *
               * El corte no es decorativo: separa la identidad del dominio de lo
               * que se puede cambiar de él, que es exactamente cómo se lee esta
               * pantalla — primero se busca el dominio, después se decide.
               */
              <article className="dash-domain-item" key={domain.id}>
                <div className="dash-domain-item-head">
                  <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-billing-row-icon is-graphite" aria-hidden="true">
                    <IconWorld size={15} />
                  </span>
                  <span className="dash-domain-item-id">
                    <strong>
                      {domain.hostname}
                      <Pill tone={status.tone}>{status.label}</Pill>
                      {/*
                        * El código de transferencia, si hay algo que decir.
                        *
                        * Aquí y no sólo dentro de la ficha porque es lo único de
                        * un dominio que la persona está ESPERANDO: lo pidió y no
                        * tiene forma de saber si ya llegó salvo abriendo la
                        * ficha de cada uno a comprobar. Un indicador en la lista
                        * convierte esa ronda en un vistazo.
                        *
                        * Pulsable, y abre la ficha: el aviso dice que hay algo,
                        * y lo siguiente que se quiere es leerlo.
                        */}
                      {domain.authCode === 'ready' ? (
                        <button
                          type="button"
                          className="dash-domain-code-flag is-ready"
                          onClick={() => { setOpen(domain) }}
                        >
                          <IconKey size={13} aria-hidden="true" /> {t('accountDomains.codeReady')}
                        </button>
                      ) : domain.authCode === 'pending' ? (
                        <button
                          type="button"
                          className="dash-domain-code-flag"
                          onClick={() => { setOpen(domain) }}
                        >
                          <IconClockCheck size={13} aria-hidden="true" /> {t('accountDomains.codeRequested')}
                        </button>
                      ) : null}
                    </strong>
                    <small>
                      {domain.expiresAt
                        ? t('accountDomains.expiresOn', { date: longDate(domain.expiresAt) })
                        : t('accountDomains.noExpiry')}
                    </small>
                  </span>
                </div>

                <div className="dash-domain-item-foot">
                  <label className="dash-domain-item-pick">
                    <span>{t('accountDomains.site')}</span>
                    <select
                      value={chosen}
                      disabled={working || domain.status === 'purchasing'}
                      onChange={(event) => { setPick((current) => ({ ...current, [domain.id]: event.target.value })) }}
                    >
                      {/* El proyecto actual va siempre, aunque ya no esté entre
                          los administrables: si no, el desplegable se abriría
                          enseñando otro sitio y parecería que el dominio se
                          movió solo. */}
                      {!domain.projectId || projects.some((project) => project.id === domain.projectId) ? null : (
                        <option value={domain.projectId}>{domain.projectName ?? domain.projectId}</option>
                      )}
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </label>

                  {/*
                    * La renovación, junto a la fecha de vencimiento y no en una
                    * pantalla aparte: la pregunta que se viene a contestar aquí
                    * es «¿esto se renueva solo o me lo tengo que acordar?», y se
                    * responde donde está la fecha.
                    */}
                  <span className="dash-domain-renewal">
                    <label className="dash-domain-renewal-switch">
                      <input
                        type="checkbox"
                        checked={domain.autoRenew}
                        disabled={busy === domain.id || domain.transferLocked}
                        onChange={() => { void toggleAutoRenew(domain) }}
                      />
                      <span>{t('accountDomains.autoRenew')}</span>
                    </label>
                    <small>
                      {domain.transferLocked
                        ? t('accountDomains.renewLocked')
                        : domain.renewalLastError
                          ? t('accountDomains.renewLastFailed')
                          : domain.autoRenew
                            ? t('accountDomains.autoRenewOn')
                            : t('accountDomains.autoRenewOff')}
                    </small>
                    {/* El botón está siempre, no sólo cuando falla: quien lo
                        apagó tiene que poder pagarlo cuando quiera. */}
                    {domain.transferLocked ? null : (
                      <button
                        type="button"
                        className="dash-secondary-btn"
                        disabled={busy === domain.id}
                        onClick={() => { void renew(domain) }}
                      >
                        {t('accountDomains.renewNow')}
                      </button>
                    )}
                  </span>

                  <span className="dash-domain-item-actions">
                    {canApply && chosen ? (
                      <button
                        type="button"
                        className="dash-secondary-btn"
                        disabled={working}
                        onClick={() => { void connect(domain, chosen) }}
                      >
                        {working
                          ? <><IconLoader2 size={14} className="dash-spin" /> {t('accountDomains.connecting')}</>
                          : <><IconLink size={14} /> {t(domain.status === 'disconnected' ? 'accountDomains.connect' : 'accountDomains.move')}</>}
                      </button>
                    ) : (
                      /* Ya está donde se eligió: se dice y no se ofrece un botón
                         que no haría nada. */
                      <span className="dash-domain-item-here"><IconCheck size={14} /> {t('accountDomains.connectedHere')}</span>
                    )}

                    {domain.projectId ? <button
                      type="button"
                      className="dash-icon-btn"
                      aria-label={t('accountDomains.settingsOf', { hostname: domain.hostname })}
                      data-tooltip={t('accountDomains.dnsAndTransfer')}
                      onClick={() => { setOpen(domain) }}
                    >
                      <IconSettings size={16} />
                    </button> : null}

                    {onOpenProject && domain.projectId && domain.status !== 'disconnected' ? (
                      <button
                        type="button"
                        className="dash-icon-btn"
                        aria-label={t('accountDomains.goTo', { project: domain.projectName ?? domain.hostname })}
                        data-tooltip={t('accountDomains.openSite')}
                        onClick={() => { onOpenProject(domain.projectId!) }}
                      >
                        <IconArrowRight size={16} />
                      </button>
                    ) : null}
                  </span>
                </div>
              </article>
            )
          })}
        </div>
      </section>
      )}

      {open ? (
        <DomainSettingsModal
          domain={asCharge(open)}
          token={token}
          onClose={() => { setOpen(null); void load() }}
        />
      ) : null}
    </div>
  )
}
